// Web Audio eerie voice chain: pitch-down/slowdown, low-pass wobble, echo, generated reverb.
export const EERIE_CONFIG = {
  playbackRate: 0.86,
  subOctaveGain: 0.12,
  lowpassHz: 2600,
  wobbleHz: 0.28,
  wobbleDepthHz: 700,
  detuneWobbleCents: 18,
  delayS: 0.34,
  feedback: 0.38,
  delayWet: 0.3,
  reverbSeconds: 3.4,
  reverbDecay: 2.4,
  reverbWet: 0.55,
  dry: 0.8,
};

let ctx = null;
let impulse = null;
let master = null;

export function getAudioContext() {
  if (!ctx) ctx = new (window.AudioContext || window.webkitAudioContext)();
  return ctx;
}

export function getMaster() {
  const c = getAudioContext();
  if (!master) {
    master = c.createGain();
    master.connect(c.destination);
  }
  return master;
}

export function setMuted(muted) {
  getMaster().gain.setTargetAtTime(muted ? 0 : 1, getAudioContext().currentTime, 0.05);
}

// Short radio-static chirp used when a line is shown without voice.
export function playTextCue() {
  const c = getAudioContext();
  const t = c.currentTime;
  [0, 0.12, 0.24].forEach((dt, i) => {
    const o = c.createOscillator();
    const g = c.createGain();
    o.type = 'square';
    o.frequency.setValueAtTime(1400 - i * 300, t + dt);
    o.frequency.exponentialRampToValueAtTime(300, t + dt + 0.09);
    g.gain.setValueAtTime(0.0001, t + dt);
    g.gain.exponentialRampToValueAtTime(0.06, t + dt + 0.01);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dt + 0.1);
    o.connect(g).connect(getMaster());
    o.start(t + dt);
    o.stop(t + dt + 0.12);
  });
}

export async function unlockAudio() {
  const c = getAudioContext();
  if (c.state === 'suspended') await c.resume();
  return c;
}

function makeImpulse(c, seconds, decay) {
  const len = Math.floor(c.sampleRate * seconds);
  const buf = c.createBuffer(2, len, c.sampleRate);
  for (let ch = 0; ch < 2; ch += 1) {
    const d = buf.getChannelData(ch);
    for (let i = 0; i < len; i += 1) d[i] = (Math.random() * 2 - 1) * (1 - i / len) ** decay;
  }
  return buf;
}

export async function decodeAudio(arrayBuffer) {
  const c = await unlockAudio();
  return c.decodeAudioData(arrayBuffer.slice(0));
}

// Gesture-free decode (works before the first user interaction).
let offline = null;
export function decodeQuiet(arrayBuffer) {
  if (!offline) offline = new (window.OfflineAudioContext || window.webkitOfflineAudioContext)(2, 1, 44100);
  return offline.decodeAudioData(arrayBuffer.slice(0));
}

export function playEerie(buffer, { fx = true, config = EERIE_CONFIG } = {}) {
  const c = getAudioContext();
  const out = c.createDynamicsCompressor();
  out.connect(getMaster());
  const src = c.createBufferSource();
  src.buffer = buffer;
  const nodes = [src];

  if (!fx) {
    src.connect(out);
  } else {
    if (!impulse) impulse = makeImpulse(c, config.reverbSeconds, config.reverbDecay);
    src.playbackRate.value = config.playbackRate;
    const lp = c.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.value = config.lowpassHz;
    lp.Q.value = 2;
    const dry = c.createGain();
    dry.gain.value = config.dry;
    const delay = c.createDelay(2);
    delay.delayTime.value = config.delayS;
    const fb = c.createGain();
    fb.gain.value = config.feedback;
    const fbLp = c.createBiquadFilter();
    fbLp.type = 'lowpass';
    fbLp.frequency.value = 1600;
    const delayWet = c.createGain();
    delayWet.gain.value = config.delayWet;
    const verb = c.createConvolver();
    verb.buffer = impulse;
    const verbWet = c.createGain();
    verbWet.gain.value = config.reverbWet;

    const lfo = c.createOscillator();
    lfo.frequency.value = config.wobbleHz;
    const lfoFilter = c.createGain();
    lfoFilter.gain.value = config.wobbleDepthHz;
    const lfoPitch = c.createGain();
    lfoPitch.gain.value = config.detuneWobbleCents;
    lfo.connect(lfoFilter).connect(lp.frequency);
    lfo.connect(lfoPitch).connect(src.detune);

    src.connect(lp);
    lp.connect(dry).connect(out);
    lp.connect(delay);
    delay.connect(fbLp).connect(fb).connect(delay);
    delay.connect(delayWet).connect(out);
    lp.connect(verb);
    delay.connect(verb);
    verb.connect(verbWet).connect(out);

    const sub = c.createBufferSource();
    sub.buffer = buffer;
    sub.playbackRate.value = config.playbackRate / 2;
    const subGain = c.createGain();
    subGain.gain.value = config.subOctaveGain;
    sub.connect(subGain).connect(lp);
    nodes.push(sub, lfo);
  }

  const rate = fx ? config.playbackRate : 1;
  const duration = buffer.duration / rate;
  const ended = new Promise((resolve) => {
    src.onended = resolve;
  });
  const t0 = c.currentTime + 0.02;
  nodes.forEach((n) => n.start(t0));
  const stopAt = t0 + duration + (fx ? config.reverbSeconds : 0);
  nodes.slice(1).forEach((n) => n.stop(stopAt));
  return {
    duration,
    ended,
    stop: () => nodes.forEach((n) => {
      try {
        n.stop();
      } catch {
        /* already stopped */
      }
    }),
  };
}
